import { Grid, Stack } from '@mui/material';
import { debounce, isNil, isString, merge, set } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Path } from 'react-hook-form';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { RichTextEditor, SwitchButtonWidget } from 'src/components/widget';
import { ISizesConfig } from 'src/interfaces';
import { lang } from 'src/utils';

import { CategoryConfigAccordionProps } from '../categoryConfigAccordion/CategoryConfigAccordion.interface';
import { ProductMoleculeItem } from '../productMoleculeItem/ProductMoleculeItem';
import { CategoryConfigItemProps } from './CategoryConfigItem.interface';

const CategoryConfigItemInner = (props: CategoryConfigItemProps) => {
  const valueRef = useRef(props.value);
  const onChangeRef = useRef(props.onChange);
  valueRef.current = props.value;
  onChangeRef.current = props.onChange;

  const globalThemeCategory = useMemo(
    () =>
      merge(
        {},
        props.theme?.config.subproducts,
        !isNil(props.variationSizeName)
          ? props.theme?.config.category?.[props.variationSizeName]
          : {},
      ),
    [
      props.theme?.config.subproducts,
      props.theme?.config.category,
      props.variationSizeName,
    ],
  );

  const debounceHandler = useMemo(
    () =>
      debounce((field: string, value: unknown) => {
        onChangeRef.current(
          set({ ...valueRef.current }, field, value) as ISizesConfig,
        );
      }, 500),
    [],
  );

  useEffect(() => () => debounceHandler.cancel(), [debounceHandler]);

  const handleOnChange = useCallback(
    (
        field: Path<CategoryConfigAccordionProps['value']>,
        enableDebounce = false,
      ) =>
      (value: unknown, fieldDetails?: unknown) => {
        if (enableDebounce) {
          debounceHandler(
            isString(fieldDetails) ? `${field}.${fieldDetails}` : field,
            value,
          );
        } else {
          onChangeRef.current(
            set(
              { ...valueRef.current } as ISizesConfig,
              isString(fieldDetails) ? `${field}.${fieldDetails}` : field,
              value,
            ),
          );
        }
      },
    [debounceHandler],
  );

  return (
    <Stack sx={{ gap: '20px', padding: '0 26px 20px', marginTop: 0 }}>
      <ProductMoleculeItem
        title={lang.sizeLabel}
        value={{
          ...globalThemeCategory?.label,
          ...props.value?.styles?.label,
        }}
        options={{
          showBgColor: props.options?.showLabelBgColor,
          showHeight: props.options?.showLabelHeight,
          showDivider: true,
          showNameOverride: props.options?.showOverrideName,
        }}
        handleChange={handleOnChange('styles.label')}
        customElement={
          <>
            {!props.options?.hideDisplayLabel && (
              <Grid item xs={12}>
                <SwitchButtonWidget
                  hasError={!!props?.errors?.displayLabel}
                  error={props?.errors?.displayLabel?.message}
                  options={{ rightLabel: lang.displayLabel }}
                  value={props.value?.displayLabel}
                  onChange={handleOnChange('displayLabel')}
                />
              </Grid>
            )}

            {props.options?.showOverrideName && (
              <Grid item xs={12}>
                <InputTextLabel>{lang.nameOverride}</InputTextLabel>
                <RichTextEditor
                  value={props.value?.nameOverride}
                  onChange={handleOnChange(`nameOverride`, true)}
                  hide={[]}
                  hideToolbar
                />
              </Grid>
            )}
          </>
        }
      />
      {props.options?.showFlavors && (
        <ProductMoleculeItem
          title={lang.productFlavor}
          value={{
            ...globalThemeCategory?.flavor,
            ...props.value?.styles?.flavor,
          }}
          options={{
            showDivider: true,
            showBrandedColorPicker: ['color'],
            colorPickerPosition: 'right',
          }}
          handleChange={handleOnChange('styles.flavor')}
        />
      )}

      <ProductMoleculeItem
        title={lang.productPrice}
        value={{
          ...globalThemeCategory?.price,
          ...props.value?.styles?.price,
        }}
        options={{
          showDivider: true,
          showBrandedColorPicker: ['color'],
          colorPickerPosition: 'right',
        }}
        handleChange={handleOnChange('styles.price')}
      />

      <ProductMoleculeItem
        title={lang.productNutrition}
        value={{
          ...globalThemeCategory?.nutrition,
          ...props.value?.styles?.nutrition,
        }}
        options={{
          showDivider: !!props.options?.showDisclaimer,
          showBrandedColorPicker: ['color'],
          colorPickerPosition: props.options?.showDisclaimer ? 'right' : 'top',
        }}
        handleChange={handleOnChange('styles.nutrition')}
      />

      {props.options?.showDisclaimer && (
        <ProductMoleculeItem
          title={lang.productDisclaimer}
          value={{
            ...globalThemeCategory?.disclaimer,
            ...props.value?.styles?.disclaimer,
          }}
          options={{
            showBrandedColorPicker: ['color'],
            colorPickerPosition: 'top',
          }}
          handleChange={handleOnChange('styles.disclaimer')}
        />
      )}
    </Stack>
  );
};

export const CategoryConfigItem = memo(CategoryConfigItemInner);
